import { Suspense, useEffect } from 'react';
import { versionGuard } from '@/utils/version-check';
import * as Sentry from '@sentry/react';
import { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  ErrorComponentProps,
  Outlet,
  useRouter,
} from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { HomeIcon, RefreshCwIcon, TriangleAlertIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

export interface RouterContext {
  queryClient: QueryClient;
}

function RootLayout() {
  return (
    <>
      <Outlet />
      <Suspense>
        <TanStackRouterDevtools />
      </Suspense>
    </>
  );
}

function RootErrorComponent({ error, reset }: ErrorComponentProps) {
  const router = useRouter();

  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  return (
    <div className='bg-muted/40 flex min-h-screen items-center justify-center px-4'>
      <Card className='w-full max-w-md shadow-lg'>
        <CardHeader className='items-center pb-2'>
          <div className='bg-destructive/10 flex size-14 items-center justify-center rounded-full'>
            <TriangleAlertIcon className='text-destructive size-7' />
          </div>
        </CardHeader>

        <CardContent className='space-y-2 text-center'>
          <h1 className='text-xl font-semibold tracking-tight'>Κάτι πήγε στραβά</h1>
          <p className='text-muted-foreground text-sm'>
            Παρουσιάστηκε ένα απρόσμενο σφάλμα. Η ομάδα μας έχει ειδοποιηθεί αυτόματα.
          </p>
        </CardContent>

        <Separator />

        <CardFooter className='justify-center gap-3 pt-6'>
          <Button variant='outline' onClick={() => router.navigate({ to: '/' })}>
            <HomeIcon />
            Αρχική
          </Button>
          <Button onClick={() => reset()}>
            <RefreshCwIcon />
            Δοκιμάστε ξανά
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: async ({ location }) => await versionGuard(location.href),
  component: RootLayout,
  errorComponent: RootErrorComponent,
});
